import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Chess } from 'chess.js';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import ChessBoardView from '@/components/chess/ChessBoardView';
import ExplanationViewer from '@/components/studies/ExplanationViewer';
import { toast } from 'sonner';
import {
  Plus, Copy, Trash2, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, Eye,
  Bold, Italic, List, GraduationCap, RotateCcw, Check, AlertTriangle, RefreshCw, Crown
} from 'lucide-react';

const START = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
const FILES = 'abcdefgh';

// Paleta de piezas (misma convención que MultipleChoiceEditor / Capture).
const PALETTE = [
  { key: 'K', label: '♔' }, { key: 'Q', label: '♕' }, { key: 'R', label: '♖' }, { key: 'B', label: '♗' }, { key: 'N', label: '♘' }, { key: 'P', label: '♙' },
  { key: 'k', label: '♚' }, { key: 'q', label: '♛' }, { key: 'r', label: '♜' }, { key: 'b', label: '♝' }, { key: 'n', label: '♞' }, { key: 'p', label: '♟' },
];

// --- Helpers FEN puros (idénticos en criterio a MC/Capture, sin motor nuevo) ---
function fenToMap(fen) {
  try {
    const [pieces] = String(fen).split(' ');
    const ranks = pieces.split('/');
    const map = {};
    for (let r = 0; r < 8; r++) {
      let f = 0;
      for (const ch of ranks[r]) {
        if (/\d/.test(ch)) f += parseInt(ch, 10);
        else { map[FILES[f] + (8 - r)] = ch; f++; }
      }
    }
    return map;
  } catch { return {}; }
}
function mapToFen(map, turn = 'w') {
  const ranks = [];
  for (let r = 8; r >= 1; r--) {
    let row = '', empty = 0;
    for (let f = 0; f < 8; f++) {
      const p = map[FILES[f] + r];
      if (p) { if (empty > 0) { row += empty; empty = 0; } row += p; }
      else empty++;
    }
    if (empty > 0) row += empty;
    ranks.push(row);
  }
  return `${ranks.join('/')} ${turn} - - 0 1`;
}
const fenTurn = (fen) => (String(fen).split(' ')[1] === 'b' ? 'b' : 'w');
const isSq = (v) => /^[a-h][1-8]$/.test(String(v || '').toLowerCase());
const fenIsValid = (f) => { try { const parts = String(f).trim().split(/\s+/); if (parts.length !== 6) return false; const [placement, turn, castling, ep, halfmove, fullmove] = parts; const ranks = placement.split("/"); if (ranks.length !== 8 || !/^[wb]$/.test(turn) || !/^(?:-|K?Q?k?q?)$/.test(castling) || !/^(?:-|[a-h][36])$/.test(ep) || !/^\d+$/.test(halfmove) || !/^\d+$/.test(fullmove)) return false; return ranks.every(rank => { let count = 0; for (const ch of rank) { if (/^[1-8]$/.test(ch)) count += Number(ch); else if (/^[prnbqkPRNBQK]$/.test(ch)) count += 1; else return false; } return count === 8; }); } catch { return false; } };
// Normaliza el challenge de un step a la forma editable del Builder.
function normChallengeForBuilder(st) {
  const ch = st?.challenge;
  if (ch && ch.type === 'move') return { type: 'move', from: String(ch.from || '').toLowerCase(), to: String(ch.to || '').toLowerCase() };
  if (ch && ch.target) return { type: 'square', target: String(ch.target).toLowerCase() };
  if (st?.mode === 'challenge') return { type: 'square', target: '' };
  return null;
}

// Configuración de las misiones pedagógicas del modo Exploración.
function normExploreForBuilder(st) {
  if (st?.mode !== 'explore') return null;
  const ex = st?.explore || {};
  const type = ['mark', 'path', 'discover'].includes(ex.type) ? ex.type : 'mark';
  const targets = Array.isArray(ex.targets)
    ? [...new Set(ex.targets.map(v => String(v || '').toLowerCase()).filter(isSq))]
    : [];
  const piece_square = isSq(ex.piece_square) ? String(ex.piece_square).toLowerCase() : '';
  return { type, targets, piece_square };
}

const safeFen = (fen) => { try { new Chess(fen); return fen; } catch { return mapToFen(fenToMap(fen), fenTurn(fen)); } };
const newId = () => `step-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

function blankBoard() {
  return { fen: START, orientation: 'white', arrows: [], circles: {}, square_styles: {}, show_coordinates: true };
}
function blankStep(n) {
  return { id: newId(), title: `Paso ${n}`, rich_text: '', board: blankBoard(), mode: 'explain' };
}

// Convierte una actividad (con o sin steps) a lista de steps editable.
function initialSteps(activity) {
  const s = Array.isArray(activity?.steps) ? activity.steps : null;
  if (s && s.length > 0) {
    return s.map((st, i) => ({
      id: st.id || newId(),
      title: st.title || `Paso ${i + 1}`,
      rich_text: st.rich_text || '',
      board: st.board ? { ...blankBoard(), ...st.board } : null,
      mode: st.mode === 'explore' ? 'explore' : (st.mode === 'challenge' ? 'challenge' : 'explain'),
      challenge: normChallengeForBuilder(st),
      explore: normExploreForBuilder(st),
    }));
  }
  // Legacy → 1 step virtual (no destruye datos; se persiste al guardar).
  if (activity && (activity.fen || activity.text || activity.title)) {
    return [{
      id: newId(),
      title: activity.title || 'Paso 1',
      rich_text: activity.text || '',
      board: activity.fen ? { ...blankBoard(), fen: activity.fen } : null,
      mode: 'explain',
    }];
  }
  return [blankStep(1)];
}

// --- Editor de texto enriquecido sencillo (contentEditable + execCommand) ---
function RichTextEditor({ value, onChange, testId }) {
  const ref = useRef(null);
  const [focused, setFocused] = useState(false);
  useEffect(() => { if (ref.current && ref.current.innerHTML !== (value || '')) ref.current.innerHTML = value || ''; /* eslint-disable-next-line */ }, []);
  const exec = (cmd) => { document.execCommand(cmd, false, null); ref.current?.focus(); onChange(ref.current?.innerHTML || ''); };
  return (
    <div className={`rounded-xl border overflow-hidden transition-colors ${focused ? 'border-blue-500 ring-2 ring-blue-100' : 'border-slate-200'}`}>
      <div className="flex items-center gap-1 bg-slate-50 border-b border-slate-200 px-2 py-1.5">
        <button type="button" data-testid="eb-rt-bold" onMouseDown={(e) => { e.preventDefault(); exec('bold'); }} className="h-7 w-7 grid place-items-center rounded-md hover:bg-blue-100 hover:text-blue-700 text-slate-600 transition-colors"><Bold className="w-3.5 h-3.5" /></button>
        <button type="button" data-testid="eb-rt-italic" onMouseDown={(e) => { e.preventDefault(); exec('italic'); }} className="h-7 w-7 grid place-items-center rounded-md hover:bg-blue-100 hover:text-blue-700 text-slate-600 transition-colors"><Italic className="w-3.5 h-3.5" /></button>
        <button type="button" data-testid="eb-rt-list" onMouseDown={(e) => { e.preventDefault(); exec('insertUnorderedList'); }} className="h-7 w-7 grid place-items-center rounded-md hover:bg-blue-100 hover:text-blue-700 text-slate-600 transition-colors"><List className="w-3.5 h-3.5" /></button>
      </div>
      <div
        ref={ref}
        data-testid={testId}
        contentEditable
        suppressContentEditableWarning
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onInput={(e) => onChange(e.currentTarget.innerHTML)}
        className="min-h-[120px] px-3 py-2.5 text-sm text-slate-700 leading-relaxed focus:outline-none [&_b]:font-bold [&_i]:italic [&_ul]:list-disc [&_ul]:pl-5"
      />
    </div>
  );
}

// --- Tarjeta de sección blanca reutilizable ---
function Section({ title, badge, children, testId }) {
  return (
    <div data-testid={testId} className="rounded-2xl bg-white border border-slate-200 shadow-sm p-4 sm:p-5">
      {title && (
        <div className="flex items-center gap-2 mb-3">
          {badge}
          <h3 className="font-display font-bold text-sm text-slate-800 uppercase tracking-wide">{title}</h3>
        </div>
      )}
      {children}
    </div>
  );
}

export default function ExplanationBuilder({ open, initial, onClose, onSave }) {
  const activity = initial?.activity || null;
  const editingId = initial?.editingId || null;
  const [title, setTitle] = useState('');
  const [steps, setSteps] = useState([]);
  const [active, setActive] = useState(0);
  const [tool, setTool] = useState(null); // null=mover · 'erase' · 'K'|'k'|...
  const [preview, setPreview] = useState(false);
  const [rtKey, setRtKey] = useState(0);

  useEffect(() => {
    if (!open) return;
    setTitle(activity?.title || 'Nueva explicación');
    setSteps(initialSteps(activity));
    setActive(0);
    setTool(null);
    setPreview(false);
    setRtKey(k => k + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activity?.id]);

  const step = steps[active] || null;

  const patchStep = useCallback((patch) => {
    setSteps(prev => prev.map((s, i) => i === active ? { ...s, ...patch } : s));
  }, [active]);

  const patchBoard = useCallback((patch) => {
    setSteps(prev => prev.map((s, i) => {
      if (i !== active) return s;
      const board = { ...(s.board || blankBoard()), ...patch };
      return { ...s, board };
    }));
  }, [active]);

  const selectStep = (i) => { setActive(i); setTool(null); setRtKey(k => k + 1); };

  const addStep = () => { setSteps(prev => { const next = [...prev, blankStep(prev.length + 1)]; setActive(next.length - 1); return next; }); setTool(null); setRtKey(k => k + 1); };
  const duplicateStep = () => {
    if (!step) return;
    setSteps(prev => {
      const copy = { ...prev[active], id: newId(), title: `${prev[active].title} (copia)`, board: prev[active].board ? { ...prev[active].board } : null };
      const next = [...prev.slice(0, active + 1), copy, ...prev.slice(active + 1)];
      setActive(active + 1); return next;
    });
    setRtKey(k => k + 1);
  };
  const deleteStep = () => {
    setSteps(prev => {
      if (prev.length <= 1) { toast.error('Debe existir al menos un paso'); return prev; }
      const next = prev.filter((_, i) => i !== active);
      setActive(Math.max(0, active - 1)); return next;
    });
    setRtKey(k => k + 1);
  };
  const moveStep = (dir) => {
    setSteps(prev => {
      const j = active + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[active], next[j]] = [next[j], next[active]];
      setActive(j); return next;
    });
  };

  // --- edición del tablero del step activo ---
  const boardFen = step?.board ? safeFen(step.board.fen) : START;
  const turn = fenTurn(step?.board?.fen || START);
  const isChallenge = step?.mode === 'challenge';
  const challengeType = step?.challenge?.type === 'move' ? 'move' : 'square';
  const exploreType = ['mark', 'path', 'discover'].includes(step?.explore?.type) ? step.explore.type : 'mark';
  const exploreTargets = Array.isArray(step?.explore?.targets) ? step.explore.targets : [];
  const currentFenValid = !step?.board || fenIsValid(step.board.fen);
  const anyFenInvalid = steps.some(s => s.board && !fenIsValid(s.board.fen));

  const onPieceDrop = (from, to) => {
    const map = fenToMap(step.board.fen); const p = map[from]; if (!p) return false;
    delete map[from]; map[to] = p; patchBoard({ fen: mapToFen(map, turn) }); return true;
  };
  const onSquareClick = (sq) => {
    if (isChallenge) {
      const s = String(sq).toLowerCase();
      if (challengeType === 'move') {
        const ch = step.challenge || {};
        if (!ch.from || (ch.from && ch.to)) patchStep({ challenge: { type: 'move', from: s, to: '' } });
        else patchStep({ challenge: { type: 'move', from: ch.from, to: s } });
      } else {
        patchStep({ challenge: { type: 'square', target: s } });
      }
      return;
    }
    if (tool !== null) {
      const map = fenToMap(step.board.fen);
      if (tool === 'erase') delete map[sq]; else map[sq] = tool;
      patchBoard({ fen: mapToFen(map, turn) });
      return;
    }
    if (step?.mode === 'explore') {
      const s = String(sq).toLowerCase();
      const ex = step.explore || { type: 'mark', targets: [], piece_square: '' };
      if (ex.type === 'discover') {
        const map = fenToMap(step.board?.fen || START);
        if (map[s]) patchStep({ explore: { ...ex, piece_square: s } });
        return;
      }
      const targets = Array.isArray(ex.targets) ? ex.targets : [];
      const nextTargets = targets.includes(s) ? targets.filter(x => x !== s) : [...targets, s];
      patchStep({ explore: { ...ex, targets: nextTargets } });
      return;
    }
  };
  const onAnnotationsChange = (arrows, circles) => patchBoard({ arrows, circles });

  // Reinicia SOLO la selección del challenge actual (no toca fen/arrows/circles/mode/otros steps).
  const resetChallenge = () => {
    patchStep({ challenge: challengeType === 'move' ? { type: 'move', from: '', to: '' } : { type: 'square', target: '' } });
  };

  const toggleBoard = (on) => {
    if (on && !step.board) patchStep({ board: blankBoard() });
    else if (!on) patchStep({ board: null });
    setTool(null);
  };

  const previewActivity = { title, steps, type: 'explanation' };

  const handleSave = () => {
    if (!title.trim()) { toast.error('Ponle un título a la explicación'); return; }
    for (const s of steps) { if (!s.title || !s.title.trim()) { toast.error('Cada paso necesita un título'); return; } }
    if (anyFenInvalid) { toast.error('Hay un FEN inválido. Corrígelo antes de guardar.'); return; }
    for (const s of steps) {
      if (s.mode === 'challenge') {
        if (!s.board) { toast.error(`El reto "${s.title}" necesita un tablero`); return; }
        const ct = s.challenge?.type === 'move' ? 'move' : 'square';
        if (ct === 'move') {
          const f = String(s.challenge?.from || '').toLowerCase();
          const t = String(s.challenge?.to || '').toLowerCase();
          if (!isSq(f) || !isSq(t)) { toast.error(`El reto "${s.title}" necesita casilla de origen y destino`); return; }
          if (f === t) { toast.error(`El reto "${s.title}": origen y destino no pueden ser iguales`); return; }
          if (!fenToMap(s.board.fen)[f]) { toast.error(`El reto "${s.title}": no hay ninguna pieza en ${f.toUpperCase()}`); return; }
        } else {
          if (!s.challenge || !s.challenge.target) { toast.error(`El reto "${s.title}" necesita una casilla objetivo`); return; }
        }
      }
    }
    for (const s of steps) {
      if (s.mode !== 'explore') continue;
      if (!s.board) { toast.error(`La misión "${s.title}" necesita un tablero`); return; }
      const et = ['mark', 'path', 'discover'].includes(s.explore?.type) ? s.explore.type : 'mark';
      if (et === 'discover') {
        const ps = String(s.explore?.piece_square || '').toLowerCase();
        if (!isSq(ps) || !fenToMap(s.board.fen)[ps]) { toast.error(`La misión "${s.title}" necesita seleccionar una pieza de partida`); return; }
      } else {
        const targets = Array.isArray(s.explore?.targets) ? s.explore.targets : [];
        if (targets.length === 0) { toast.error(`La misión "${s.title}" necesita al menos una casilla objetivo`); return; }
      }
    }
    const clean = steps.map(s => ({
      id: s.id, title: s.title.trim(), rich_text: s.rich_text || '',
      mode: s.mode === 'explore' ? 'explore' : (s.mode === 'challenge' ? 'challenge' : 'explain'),
      board: s.board ? {
        fen: s.board.fen || START,
        orientation: s.board.orientation === 'black' ? 'black' : 'white',
        arrows: Array.isArray(s.board.arrows) ? s.board.arrows : [],
        circles: s.board.circles && typeof s.board.circles === 'object' ? s.board.circles : {},
        square_styles: s.board.square_styles && typeof s.board.square_styles === 'object' ? s.board.square_styles : {},
        show_coordinates: s.board.show_coordinates !== false,
      } : null,
      challenge: s.mode === 'challenge'
        ? (s.challenge?.type === 'move'
            ? { type: 'move', from: String(s.challenge?.from || '').toLowerCase(), to: String(s.challenge?.to || '').toLowerCase() }
            : { type: 'square', target: String(s.challenge?.target || '').toLowerCase() })
        : null,
      explore: s.mode === 'explore'
        ? {
            type: ['mark', 'path', 'discover'].includes(s.explore?.type) ? s.explore.type : 'mark',
            targets: Array.isArray(s.explore?.targets) ? [...new Set(s.explore.targets.map(v => String(v || '').toLowerCase()).filter(isSq))] : [],
            piece_square: isSq(s.explore?.piece_square) ? String(s.explore.piece_square).toLowerCase() : '',
          }
        : null,
    }));
    const payload = {
      type: 'explanation',
      title: title.trim(),
      steps: clean,
      points: activity?.points ?? 10,
      max_attempts: activity?.max_attempts ?? 3,
      time_seconds: activity?.time_seconds ?? 60,
    };
    onSave(payload, editingId);
  };

  if (!open) return null;
  const hasBoard = !!step?.board;

  const modeOptions = [
    { key: 'explain', label: 'Explicación', desc: 'El alumno observa el tablero.', testId: 'eb-mode-explain' },
    { key: 'explore', label: 'Exploración', desc: 'El alumno completa una misión de descubrimiento.', testId: 'eb-mode-explore' },
    { key: 'challenge', label: 'Reto', desc: 'El alumno debe completar una acción.', testId: 'eb-mode-challenge' },
  ];
  const currentMode = step?.mode || 'explain';

  return (
    <>
      <Dialog open={open && !preview} onOpenChange={(v) => { if (!v) onClose(); }}>
        <DialogContent data-testid="explanation-builder" className="max-w-6xl p-0 overflow-hidden gap-0 bg-slate-50 max-h-[94vh] flex flex-col">
          <DialogTitle className="sr-only">Constructor de explicación</DialogTitle>
          <DialogDescription className="sr-only">Construye una clase de ajedrez por pasos.</DialogDescription>

          {/* Cabecera — blanca, azul Chess4Kings, detalle dorado */}
          <div className="bg-white border-b border-slate-200 px-5 sm:px-6 py-3.5 shrink-0">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 rounded-lg bg-blue-600 grid place-items-center">
                <Crown className="w-4 h-4 text-amber-300" />
              </div>
              <span className="text-[11px] font-bold uppercase tracking-widest text-slate-400">Chess4Kings · Constructor de clase</span>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-end gap-2">
              <div className="flex-1">
                <h2 className="font-display font-extrabold text-lg sm:text-xl text-blue-700 leading-tight">{editingId ? 'Editar explicación' : 'Crear explicación'}</h2>
                <p className="text-xs text-slate-500">Construye tu clase paso a paso, con tablero y retos.</p>
              </div>
              <Input data-testid="eb-activity-title" value={title} onChange={e => setTitle(e.target.value)} placeholder="Título de la explicación"
                className="sm:w-80 font-display font-semibold border-slate-300 focus-visible:ring-blue-200 focus-visible:border-blue-500" />
            </div>
          </div>

          <div className="grid md:grid-cols-[240px_1fr] min-h-0 flex-1 overflow-hidden">
            {/* Sidebar de pasos */}
            <div className="border-b md:border-b-0 md:border-r border-slate-200 bg-white flex flex-col min-h-0">
              <div className="px-3 py-2.5 text-[11px] font-bold uppercase tracking-widest text-slate-500 flex items-center justify-between">
                Pasos de la clase
                <button data-testid="eb-add-step" onClick={addStep} title="Agregar paso" className="h-6 w-6 grid place-items-center rounded-md bg-blue-600 text-white hover:bg-blue-700 transition-colors"><Plus className="w-3.5 h-3.5" /></button>
              </div>
              <div className="flex md:flex-col gap-1.5 md:gap-1 overflow-x-auto md:overflow-x-visible md:flex-1 md:overflow-y-auto px-2 pb-2" data-testid="eb-steps-list">
                {steps.map((s, i) => {
                  const isActive = i === active;
                  return (
                    <button key={s.id} data-testid={`eb-step-${i}`} onClick={() => selectStep(i)}
                      className={`shrink-0 md:w-full text-left rounded-xl px-2.5 py-2 flex items-center gap-2.5 border transition-all ${isActive ? 'bg-blue-50 border-blue-500 shadow-sm' : 'bg-white border-slate-200 hover:border-slate-300 hover:bg-slate-50'}`}>
                      <span className={`w-7 h-7 shrink-0 grid place-items-center rounded-lg text-[11px] font-extrabold ${isActive ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500'}`}>{String(i + 1).padStart(2, '0')}</span>
                      <span className="min-w-0 md:flex-1">
                        <span className={`block truncate text-sm font-semibold ${isActive ? 'text-blue-800' : 'text-slate-700'}`}>{s.title || 'Sin título'}</span>
                        <span className="block truncate text-[11px] text-slate-400">{!s.board ? 'Sólo texto' : (s.mode === 'challenge' ? 'Reto' : s.mode === 'explore' ? 'Exploración' : 'Explicación')}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
              <div className="border-t border-slate-200 p-2 grid grid-cols-4 gap-1.5">
                <button data-testid="eb-move-up" onClick={() => moveStep(-1)} disabled={active === 0} title="Subir" className="h-8 grid place-items-center rounded-lg border border-slate-200 bg-white hover:bg-blue-50 hover:text-blue-700 hover:border-blue-300 text-slate-600 disabled:opacity-40 transition-colors"><ArrowUp className="w-4 h-4" /></button>
                <button data-testid="eb-move-down" onClick={() => moveStep(1)} disabled={active === steps.length - 1} title="Bajar" className="h-8 grid place-items-center rounded-lg border border-slate-200 bg-white hover:bg-blue-50 hover:text-blue-700 hover:border-blue-300 text-slate-600 disabled:opacity-40 transition-colors"><ArrowDown className="w-4 h-4" /></button>
                <button data-testid="eb-duplicate" onClick={duplicateStep} title="Duplicar" className="h-8 grid place-items-center rounded-lg border border-slate-200 bg-white hover:bg-blue-50 hover:text-blue-700 hover:border-blue-300 text-slate-600 transition-colors"><Copy className="w-4 h-4" /></button>
                <button data-testid="eb-delete" onClick={deleteStep} title="Eliminar" className="h-8 grid place-items-center rounded-lg border border-rose-200 bg-white text-rose-600 hover:bg-rose-50 transition-colors"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>

            {/* Editor del step activo */}
            <div className="min-h-0 overflow-y-auto p-4 sm:p-5">
              {step && (
                <div className="grid lg:grid-cols-2 gap-4 sm:gap-5">
                  {/* Columna izquierda: tablero + herramientas + FEN */}
                  <div className="space-y-4">
                    <Section testId="eb-board-section" title="Tablero" badge={<span className="w-2 h-2 rounded-full bg-blue-600" />}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Switch data-testid="eb-toggle-board" checked={hasBoard} onCheckedChange={toggleBoard} />
                          <Label className="text-sm text-slate-600">Mostrar tablero</Label>
                        </div>
                      </div>
                      {hasBoard ? (
                        <div className="space-y-3">
                          <div className="grid place-items-center rounded-xl bg-slate-50 border border-slate-200 p-3">
                            <ChessBoardView
                              fen={boardFen}
                              orientation={step.board.orientation === 'black' ? 'black' : 'white'}
                              boardWidth={320}
                              arePiecesDraggable={tool === null && !isChallenge}
                              allowAnnotations={!isChallenge}
                              arrows={step.board.arrows || []}
                              circles={step.board.circles || {}}
                              extraSquareStyles={isChallenge ? { ...(step.board.square_styles || {}), ...(challengeType === 'move'
                                 ? { ...(step.challenge?.from ? { [step.challenge.from]: { boxShadow: 'inset 0 0 0 4px rgba(16,185,129,0.9)' } } : {}), ...(step.challenge?.to ? { [step.challenge.to]: { boxShadow: 'inset 0 0 0 4px rgba(59,130,246,0.9)' } } : {}) }
                                 : (step.challenge?.target ? { [step.challenge.target]: { boxShadow: 'inset 0 0 0 4px rgba(245,158,11,0.9)' } } : {})) } : (step.mode === 'explore' ? { ...(step.board.square_styles || {}), ...Object.fromEntries(exploreTargets.map(t => [t, { backgroundColor: 'rgba(16,185,129,0.18)', boxShadow: 'inset 0 0 0 3px rgba(16,185,129,0.75)' }])), ...(step.explore?.piece_square ? { [step.explore.piece_square]: { boxShadow: 'inset 0 0 0 4px rgba(245,158,11,0.95)' } } : {}) } : (step.board.square_styles || {}))}

                              onPieceDrop={onPieceDrop}
                              onSquareClick={onSquareClick}
                              onAnnotationsChange={onAnnotationsChange}
                            />
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            <Button size="sm" variant="outline" data-testid="eb-preset-start" onClick={() => patchBoard({ fen: START })}>Posición inicial</Button>
                            <Button size="sm" variant="outline" data-testid="eb-preset-clear" onClick={() => patchBoard({ fen: mapToFen({}, turn) })}>Vaciar</Button>
                            <Button size="sm" variant="outline" data-testid="eb-orientation" onClick={() => patchBoard({ orientation: step.board.orientation === 'black' ? 'white' : 'black' })}><RotateCcw className="w-3.5 h-3.5 mr-1" /> Girar ({step.board.orientation === 'black' ? 'Negras' : 'Blancas'})</Button>
                            <Button size="sm" variant={turn === 'w' ? 'default' : 'outline'} className={turn === 'w' ? 'bg-blue-600 hover:bg-blue-700' : ''} data-testid="eb-turn-w" onClick={() => patchBoard({ fen: mapToFen(fenToMap(step.board.fen), 'w') })}>Turno ♙</Button>
                            <Button size="sm" variant={turn === 'b' ? 'default' : 'outline'} className={turn === 'b' ? 'bg-blue-600 hover:bg-blue-700' : ''} data-testid="eb-turn-b" onClick={() => patchBoard({ fen: mapToFen(fenToMap(step.board.fen), 'b') })}>Turno ♟</Button>
                          </div>
                          {/* Herramientas */}
                          <div className={`rounded-xl bg-slate-50 border border-slate-200 p-2.5 space-y-2 ${isChallenge ? 'opacity-50 pointer-events-none' : ''}`}>
                            <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Herramienta</div>
                            <div className="flex flex-wrap gap-1.5">
                              <button type="button" data-testid="eb-tool-move" onClick={() => setTool(null)} className={`h-8 px-3 rounded-lg text-xs font-semibold border transition-colors ${tool === null ? 'bg-blue-600 text-white border-blue-600' : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-100'}`}>Mover</button>
                              <button type="button" data-testid="eb-tool-erase" onClick={() => setTool('erase')} className={`h-8 px-3 rounded-lg text-xs font-semibold border transition-colors ${tool === 'erase' ? 'bg-rose-600 text-white border-rose-600' : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-100'}`}>Borrar</button>
                              {PALETTE.map(p => (
                                <button type="button" key={p.key} data-testid={`eb-tool-${p.key}`} onClick={() => setTool(p.key)} title={`Colocar ${p.key}`} className={`h-8 w-8 rounded-lg text-lg leading-none border transition-colors ${tool === p.key ? 'bg-amber-400 border-amber-500' : 'bg-white border-slate-200 hover:bg-slate-100'}`}>{p.label}</button>
                              ))}
                            </div>
                            <div className="text-[10px] text-slate-400"><b>Mover</b>: arrastra piezas. Elige pieza y haz clic para colocar. <b>Flechas/círculos</b>: clic derecho sobre el tablero.</div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Switch data-testid="eb-show-coords" checked={step.board.show_coordinates !== false} onCheckedChange={(v) => patchBoard({ show_coordinates: v })} />
                            <Label className="text-xs text-slate-600">Mostrar coordenadas</Label>
                          </div>
                          {/* FEN con validación */}
                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <Label className="text-[11px] text-slate-500">FEN</Label>
                              {currentFenValid
                                ? <span data-testid="eb-fen-status" className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600"><Check className="w-3.5 h-3.5" /> FEN válido</span>
                                : <span data-testid="eb-fen-status" className="inline-flex items-center gap-1 text-[11px] font-semibold text-rose-600"><AlertTriangle className="w-3.5 h-3.5" /> FEN no válido</span>}
                            </div>
                            <Input data-testid="eb-fen" className={`font-mono text-[11px] ${currentFenValid ? 'border-slate-200 focus-visible:border-blue-500 focus-visible:ring-blue-200' : 'border-rose-400 focus-visible:ring-rose-200'}`} value={step.board.fen} onChange={e => patchBoard({ fen: e.target.value })} />
                          </div>
                        </div>
                      ) : (
                        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 grid place-items-center text-center text-sm text-slate-400 py-14 px-4">
                          Este paso es sólo texto.<br />Activa «Mostrar tablero» para agregar una posición.
                        </div>
                      )}
                    </Section>
                  </div>

                  {/* Columna derecha: modo + texto + reto */}
                  <div className="space-y-4">
                    <Section testId="eb-step-meta" title="Paso" badge={<span className="w-2 h-2 rounded-full bg-amber-400" />}>
                      <Label className="text-xs text-slate-500">Título del paso</Label>
                      <Input data-testid="eb-step-title" value={step.title} onChange={e => patchStep({ title: e.target.value })} placeholder="Ej: ¿Qué son las columnas?" className="mt-1 focus-visible:border-blue-500 focus-visible:ring-blue-200" />
                    </Section>

                    <Section testId="eb-mode-section" title="Modo del paso" badge={<span className="w-2 h-2 rounded-full bg-blue-600" />}>
                      <div className="grid grid-cols-3 gap-2">
                        {modeOptions.map(m => {
                          const sel = currentMode === m.key;
                          const onSel = () => {
                            if (m.key === 'challenge') patchStep({ mode: 'challenge', board: step.board || blankBoard(), challenge: step.challenge || { type: 'square', target: '' } });
                            else if (m.key === 'explore') patchStep({ mode: 'explore', board: step.board || blankBoard(), explore: step.explore || { type: 'mark', targets: [], piece_square: '' } });
                            else patchStep({ mode: m.key });
                          };
                          return (
                            <button key={m.key} type="button" data-testid={m.testId} onClick={onSel}
                              className={`text-left rounded-xl border p-2.5 transition-all ${sel ? 'bg-blue-50 border-blue-500 ring-1 ring-blue-200' : 'bg-white border-slate-200 hover:border-slate-300'}`}>
                              <div className={`text-sm font-bold ${sel ? 'text-blue-700' : 'text-slate-700'}`}>{m.label}</div>
                              <div className="text-[11px] text-slate-400 mt-0.5 leading-snug">{m.desc}</div>
                            </button>
                          );
                        })}
                      </div>
                      {isChallenge && (
                        <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 space-y-2.5" data-testid="eb-challenge-config">
                          <div className="flex items-center justify-between">
                            <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">Configuración del reto</div>
                            <button type="button" data-testid="eb-challenge-reset" onClick={resetChallenge} className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-800 hover:text-amber-900 bg-white/70 border border-amber-300 rounded-md px-2 py-1 transition-colors"><RefreshCw className="w-3 h-3" /> Reiniciar selección</button>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <button type="button" data-testid="eb-challenge-type-square" onClick={() => patchStep({ challenge: { type: 'square', target: step.challenge?.target || '' } })}
                              className={`text-left rounded-lg border p-2 transition-all ${challengeType === 'square' ? 'bg-white border-amber-500 ring-1 ring-amber-300' : 'bg-white/60 border-amber-200 hover:border-amber-300'}`}>
                              <div className="text-sm font-bold text-amber-900">Casilla objetivo</div>
                              <div className="text-[11px] text-amber-800">Clic en la casilla correcta.</div>
                            </button>
                            <button type="button" data-testid="eb-challenge-type-move" onClick={() => patchStep({ challenge: { type: 'move', from: '', to: '' } })}
                              className={`text-left rounded-lg border p-2 transition-all ${challengeType === 'move' ? 'bg-white border-amber-500 ring-1 ring-amber-300' : 'bg-white/60 border-amber-200 hover:border-amber-300'}`}>
                              <div className="text-sm font-bold text-amber-900">Movimiento</div>
                              <div className="text-[11px] text-amber-800">Origen → destino.</div>
                            </button>
                          </div>
                          {challengeType === 'move' ? (
                            <div className="pt-1 border-t border-amber-200 space-y-1">
                              <div className="text-xs text-amber-800">Haz clic en el tablero: 1º <b>origen</b>, 2º <b>destino</b>.</div>
                              <div className="text-sm">Origen: <span data-testid="eb-challenge-from" className="font-mono font-bold text-emerald-700">{step.challenge?.from ? step.challenge.from.toUpperCase() : '— (elige origen)'}</span> <span className="text-amber-700">→</span> Destino: <span data-testid="eb-challenge-to" className="font-mono font-bold text-blue-700">{step.challenge?.to ? step.challenge.to.toUpperCase() : '— (elige destino)'}</span></div>
                            </div>
                          ) : (
                            <div className="pt-1 border-t border-amber-200 space-y-1">
                              <div className="text-xs text-amber-800">Haz clic en una casilla del tablero para fijar el objetivo.</div>
                              <div className="text-sm">Objetivo: <span data-testid="eb-challenge-target" className="font-mono font-bold text-amber-900">{step.challenge?.target ? step.challenge.target.toUpperCase() : '— (elige una casilla)'}</span></div>
                            </div>
                          )}
                        </div>
                      )}
                    </Section>

                    {step.mode === 'explore' && (
                      <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3 space-y-3" data-testid="eb-explore-config">
                        <div className="text-[11px] font-bold uppercase tracking-widest text-emerald-700">Configuración de la misión</div>
                        <div className="grid grid-cols-3 gap-2">
                          {[
                            { key: 'mark', label: 'Marca casillas', desc: 'El alumno selecciona objetivos.' },
                            { key: 'path', label: 'Recorrido', desc: 'Debe visitar objetivos con movimientos legales.' },
                            { key: 'discover', label: 'Descubrimiento', desc: 'Descubre los movimientos legales de una pieza.' },
                          ].map(opt => (
                            <button key={opt.key} type="button" data-testid={`eb-explore-type-${opt.key}`}
                              onClick={() => patchStep({ explore: { ...(step.explore || {}), type: opt.key, targets: opt.key === 'discover' ? [] : (step.explore?.targets || []), piece_square: opt.key === 'discover' ? (step.explore?.piece_square || '') : '' } })}
                              className={`text-left rounded-lg border p-2 transition-all ${exploreType === opt.key ? 'bg-white border-emerald-500 ring-1 ring-emerald-300' : 'bg-white/60 border-emerald-200 hover:border-emerald-300'}`}>
                              <div className="text-sm font-bold text-emerald-900">{opt.label}</div>
                              <div className="text-[10px] text-emerald-800 mt-0.5 leading-snug">{opt.desc}</div>
                            </button>
                          ))}
                        </div>
                        {exploreType === 'discover' ? (
                          <div className="pt-2 border-t border-emerald-200 text-xs text-emerald-800">
                            Haz clic sobre la <b>pieza</b> que el alumno deberá analizar. La pieza permanecerá fija durante la misión.
                            <div className="mt-1 text-sm">Pieza: <span data-testid="eb-explore-piece" className="font-mono font-bold text-amber-700">{step.explore?.piece_square ? step.explore.piece_square.toUpperCase() : '— (selecciona una pieza)'}</span></div>
                          </div>
                        ) : (
                          <div className="pt-2 border-t border-emerald-200 text-xs text-emerald-800">
                            Haz clic en las <b>casillas objetivo</b> del tablero para agregarlas o quitarlas. El orden no importa.
                            <div className="mt-1 text-sm">Objetivos: <span data-testid="eb-explore-target-count" className="font-bold">{exploreTargets.length}</span></div>
                          </div>
                        )}
                      </div>
                    )}

                    <Section testId="eb-content-section" title="Contenido" badge={<span className="w-2 h-2 rounded-full bg-amber-400" />}>
                      <RichTextEditor key={`${step.id}-${rtKey}`} testId="eb-step-richtext" value={step.rich_text} onChange={(html) => patchStep({ rich_text: html })} />
                    </Section>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between gap-2 border-t border-slate-200 px-4 sm:px-5 py-3 bg-white shrink-0">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" data-testid="eb-prev" onClick={() => selectStep(Math.max(0, active - 1))} disabled={active === 0}><ChevronLeft className="w-4 h-4 mr-1" /> Anterior</Button>
              <Button variant="outline" size="sm" data-testid="eb-next" onClick={() => selectStep(Math.min(steps.length - 1, active + 1))} disabled={active === steps.length - 1}>Siguiente <ChevronRight className="w-4 h-4 ml-1" /></Button>
              <span className="text-xs text-slate-500 ml-1 hidden sm:inline">Paso {active + 1} de {steps.length}</span>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" data-testid="eb-preview" onClick={() => setPreview(true)} className="border-blue-200 text-blue-700 hover:bg-blue-50"><Eye className="w-4 h-4 mr-1" /> Vista previa</Button>
              <Button variant="ghost" size="sm" data-testid="eb-cancel" onClick={onClose}>Cancelar</Button>
              <Button size="sm" data-testid="eb-save" disabled={anyFenInvalid} className="bg-blue-600 hover:bg-blue-700 text-white ring-1 ring-amber-300/60 disabled:opacity-50 disabled:cursor-not-allowed" onClick={handleSave}>{editingId ? 'Guardar cambios' : 'Guardar explicación'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Vista previa: reutiliza el ExplanationViewer real del alumno */}
      <ExplanationViewer open={open && preview} activity={previewActivity} onClose={() => setPreview(false)} />
    </>
  );
}
